import axios from 'axios';
import {
  Project,
  ProjectWithTasks,
  TaskWithAssignees,
  CreateProjectRequest,
  UpdateProjectRequest,
  CreateTaskRequest,
  UpdateTaskRequest,
  ProjectStatusHistoryEntry,
} from '@/types/project';

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || '/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const projectService = {
  // ── Projects ──────────────────────────────────────────────────────

  async getProjects(filters?: { status?: string; search?: string; created_by?: number }): Promise<Project[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.created_by) params.append('created_by', filters.created_by.toString());
    const res = await apiClient.get(`/projects?${params.toString()}`);
    return res.data.projects;
  },

  async getProject(id: number): Promise<ProjectWithTasks> {
    const res = await apiClient.get(`/projects/${id}`);
    return res.data.project;
  },

  async createProject(data: CreateProjectRequest): Promise<Project> {
    const res = await apiClient.post('/projects', data);
    return res.data.project;
  },

  async updateProject(id: number, data: UpdateProjectRequest): Promise<Project> {
    const res = await apiClient.put(`/projects/${id}`, data);
    return res.data.project;
  },

  async deleteProject(id: number): Promise<void> {
    await apiClient.delete(`/projects/${id}`);
  },

  async getProjectHistory(id: number): Promise<ProjectStatusHistoryEntry[]> {
    const res = await apiClient.get(`/projects/${id}/history`);
    return res.data.history;
  },

  // ── Tasks ─────────────────────────────────────────────────────────

  async getTasks(filters?: {
    project_id?: number;
    status?: string;
    shift_type_id?: number;
    priority?: string;
    assigned_to?: number;
    due_date_from?: string;
    due_date_to?: string;
  }): Promise<TaskWithAssignees[]> {
    const params = new URLSearchParams();
    if (filters?.project_id) params.append('project_id', filters.project_id.toString());
    if (filters?.status) params.append('status', filters.status);
    if (filters?.shift_type_id) params.append('shift_type_id', filters.shift_type_id.toString());
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.assigned_to) params.append('assigned_to', filters.assigned_to.toString());
    if (filters?.due_date_from) params.append('due_date_from', filters.due_date_from);
    if (filters?.due_date_to) params.append('due_date_to', filters.due_date_to);
    const res = await apiClient.get(`/projects/tasks?${params.toString()}`);
    return res.data.tasks;
  },

  async getTask(id: number): Promise<TaskWithAssignees> {
    const res = await apiClient.get(`/projects/tasks/${id}`);
    return res.data.task;
  },

  async createTask(data: CreateTaskRequest): Promise<TaskWithAssignees> {
    const res = await apiClient.post('/projects/tasks', data);
    return res.data.task;
  },

  async updateTask(id: number, data: UpdateTaskRequest): Promise<TaskWithAssignees> {
    const res = await apiClient.put(`/projects/tasks/${id}`, data);
    return res.data.task;
  },

  async deleteTask(id: number): Promise<void> {
    await apiClient.delete(`/projects/tasks/${id}`);
  },

  async updateTaskStatus(id: number, status: string): Promise<TaskWithAssignees> {
    const res = await apiClient.patch(`/projects/tasks/${id}/status`, { status });
    return res.data.task;
  },

  // ── Assignments ───────────────────────────────────────────────────

  async assignEmployee(taskId: number, employeeId: number): Promise<TaskWithAssignees> {
    const res = await apiClient.post(`/projects/tasks/${taskId}/assign`, { employee_id: employeeId });
    return res.data.task;
  },

  async unassignEmployee(taskId: number, employeeId: number): Promise<TaskWithAssignees> {
    const res = await apiClient.delete(`/projects/tasks/${taskId}/unassign/${employeeId}`);
    return res.data.task;
  },

  async getEmployeeTasks(employeeId: number): Promise<TaskWithAssignees[]> {
    const res = await apiClient.get(`/projects/employee/${employeeId}/tasks`);
    return res.data.tasks;
  },
};
